<script>
/* eslint no-console: 0 */
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    files: { type: Array, default: () => [] },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      attachedFiles: [...this.files],
      removedBlobIds: [],
      isUploadingFile: false,
      show: true,
    };
  },
  validations() {
    return {
      shortCode: {
        required,
        minLength: minLength(2),
      },
      content: {
        required: value =>
          !!String(value || '').trim() || this.attachedFiles.length > 0,
      },
    };
  },
  computed: {
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
  },
  methods: {
    setPageName({ name }) {
      this.v$.content.$touch();
      this.content = name;
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.attachedFiles = [];
      this.removedBlobIds = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    openFilePicker() {
      this.$refs.fileInput?.click();
    },
    async uploadCannedFile(event) {
      const selectedFiles = Array.from(event.target.files || []);
      if (!selectedFiles.length) return;

      this.isUploadingFile = true;
      try {
        const uploadedFiles = await Promise.all(
          selectedFiles.map(async file => {
            const { fileUrl, blobId } = await uploadFile(file);
            return {
              id: blobId,
              blob_id: blobId,
              blob_signed_id: blobId,
              file_url: fileUrl,
              filename: file.name,
              file_type: file.type,
              byte_size: file.size,
            };
          })
        );
        this.attachedFiles = [...this.attachedFiles, ...uploadedFiles];
        this.v$.content.$touch();
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error ||
          this.$t('CANNED_MGMT.ATTACHMENTS.ERROR');
        useAlert(errorMessage);
      } finally {
        this.isUploadingFile = false;
        event.target.value = '';
      }
    },
    removeFile(fileToRemove) {
      if (Number.isInteger(fileToRemove.blob_id)) {
        this.removedBlobIds = [...this.removedBlobIds, fileToRemove.blob_id];
      }
      this.attachedFiles = this.attachedFiles.filter(
        file => file.id !== fileToRemove.id
      );
      this.v$.content.$touch();
    },
    isImage(file) {
      return file.file_type?.includes('image');
    },
    editCannedResponse() {
      this.v$.$touch();
      if (this.v$.$invalid) return;

      // Show loading on button
      this.editCanned.showLoading = true;
      // Make API Calls
      this.$store
        .dispatch('updateCannedResponse', {
          id: this.id,
          short_code: this.shortCode,
          content: this.content,
          blob_ids: this.attachedFiles
            .map(file => file.blob_signed_id)
            .filter(Boolean),
          removed_blob_ids: this.removedBlobIds,
        })
        .then(() => {
          // Reset Form, Show success message
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              :class="{ editor_warning: v$.content.$error }"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>
        <div class="w-full">
          <input
            ref="fileInput"
            type="file"
            multiple
            class="hidden"
            accept="image/*"
            @change="uploadCannedFile"
          />
          <div class="flex items-center gap-2 py-2">
            <NextButton
              type="button"
              slate
              faded
              icon="i-lucide-image-plus"
              :label="$t('CANNED_MGMT.ATTACHMENTS.ADD')"
              :is-loading="isUploadingFile"
              @click.prevent="openFilePicker"
            />
          </div>
          <div v-if="attachedFiles.length" class="flex flex-wrap gap-2 pb-2">
            <div
              v-for="file in attachedFiles"
              :key="file.id"
              class="flex items-center gap-2 max-w-full px-2 py-1 rounded-md bg-n-slate-3"
            >
              <img
                v-if="isImage(file)"
                :src="file.file_url"
                :alt="file.filename"
                class="object-cover rounded-sm size-8"
              />
              <span v-else class="i-lucide-file text-lg" />
              <span class="text-sm truncate max-w-48 text-n-slate-12">
                {{ file.filename }}
              </span>
              <NextButton
                type="button"
                ghost
                slate
                xs
                icon="i-lucide-x"
                @click.prevent="removeFile(file)"
              />
            </div>
          </div>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="
              v$.content.$invalid ||
              v$.shortCode.$invalid ||
              isUploadingFile ||
              editCanned.showLoading
            "
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}
</style>
